"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section, Logo } from "@/src/components";
import { CodeBlock } from "../../_components/code-block";

export default function BrandLogoPage() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Logo Guidelines
            </Text>
            <Badge variant="secondary">Brand</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Our logo is the most recognizable element of our brand. Use it
            consistently to maintain brand integrity.
          </Text>
        </div>
      </Section>

      {/* Primary Logo */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Primary Logo</CardTitle>
              <CardDescription>The default logo for most applications</CardDescription>
            </>
          }
          contentClassName="p-8"
        >
          <div className="flex flex-col items-center gap-8">
            <div className="flex items-center gap-6 p-8 bg-muted/30 rounded-xl">
              <Logo size={48} />
              <div>
                <Text className="text-h4 font-bold">Dream Light</Text>
              </div>
            </div>
            <CodeBlock
              code={`import Logo from "@/registry/new-york/ui/brand-logo";

<Logo size={48} />`}
              language="tsx"
            />
          </div>
        </SectionCard>
      </Section>

      {/* Logo Sizes */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Logo Sizes</CardTitle>
              <CardDescription>Approved size variations</CardDescription>
            </>
          }
          contentClassName="p-8"
        >
          <div className="flex items-end justify-center gap-12">
            <div className="flex flex-col items-center gap-2">
              <Logo size={24} />
              <Text className="text-xs text-muted-foreground">24px - Small</Text>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Logo size={32} />
              <Text className="text-xs text-muted-foreground">32px - Default</Text>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Logo size={48} />
              <Text className="text-xs text-muted-foreground">48px - Large</Text>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Logo size={64} />
              <Text className="text-xs text-muted-foreground">64px - XLarge</Text>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Clear Space */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Clear Space</CardTitle>
              <CardDescription>Minimum space around the logo</CardDescription>
            </>
          }
          contentClassName="p-8"
        >
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <div className="absolute inset-0 border-2 border-dashed border-primary/30 -m-6 rounded-lg" />
              <div className="p-6 bg-muted/30 rounded-lg">
                <Logo size={48} />
              </div>
            </div>
            <Text className="text-sm text-muted-foreground text-center max-w-md">
              Always maintain a minimum clear space equal to the height of the logo
              mark on all sides. This ensures the logo remains visible and impactful.
            </Text>
          </div>
        </SectionCard>
      </Section>

      {/* Background Usage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Background Usage</CardTitle>
              <CardDescription>Logo on different backgrounds</CardDescription>
            </>
          }
          contentClassName="p-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center justify-center w-full h-24 bg-background rounded-lg border">
                <Logo size={32} />
              </div>
              <Text className="text-xs text-muted-foreground">Light background</Text>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center justify-center w-full h-24 bg-foreground rounded-lg">
                <Logo size={32} />
              </div>
              <Text className="text-xs text-muted-foreground">Dark background</Text>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center justify-center w-full h-24 bg-primary rounded-lg">
                <div className="bg-background p-4 rounded-full">
                  <Logo
                    size={32}
                  />
                </div>
              </div>
              <Text className="text-xs text-muted-foreground">Brand background</Text>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Misuse Examples */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle className="text-destructive">Logo Misuse</CardTitle>
              <CardDescription>What NOT to do with the logo</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex flex-col items-center gap-3 p-4 bg-destructive/5 rounded-lg border border-destructive/20">
              <div className="opacity-30">
                <Logo size={32} />
              </div>
              <Text className="text-xs text-center text-destructive">
                Don't reduce opacity
              </Text>
            </div>
            <div className="flex flex-col items-center gap-3 p-4 bg-destructive/5 rounded-lg border border-destructive/20">
              <div className="scale-x-150">
                <Logo size={32} />
              </div>
              <Text className="text-xs text-center text-destructive">
                Don't stretch
              </Text>
            </div>
            <div className="flex flex-col items-center gap-3 p-4 bg-destructive/5 rounded-lg border border-destructive/20">
              <div className="rotate-45">
                <Logo size={32} />
              </div>
              <Text className="text-xs text-center text-destructive">
                Don't rotate
              </Text>
            </div>
            <div className="flex flex-col items-center gap-3 p-4 bg-destructive/5 rounded-lg border border-destructive/20">
              <div className="drop-shadow-lg">
                <Logo size={32} />
              </div>
              <Text className="text-xs text-center text-destructive">
                Don't add effects
              </Text>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Usage in Code */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Usage</CardTitle>
              <CardDescription>How to use the logo component</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <CodeBlock
            code={`import Logo from "@/registry/new-york/ui/brand-logo";

// Default size (32px)
<Logo />

// Custom size
<Logo size={48} />

// With text
<div className="flex items-center gap-2">
  <Logo size={32} />
  <div className="font-bold">Dream Light</div>
</div>

// On brand/primary background (inverted colors)
<div className="bg-primary p-4">
  <Logo 
    size={32} 
    primary="#fff" 
    secondary="rgba(255,255,255,0.6)" 
    contrast="var(--primary)" 
  />
</div>`}
            language="tsx"
            showLineNumbers
          />
        </SectionCard>
      </Section>
    </>
  );
}
